import React, { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { operatorApiFetch } from '@services/operator/apiService';
import soundEffects from '@utils/soundEffects';

interface Product {
  id: string;
  sku: string;
  name: string;
  barcode?: string | null;
  secondary_barcodes?: string[] | null;
  validity_days?: number | null;
  batch_management_required?: boolean;
  manufacturing_date_required?: boolean;
  required_serial_numbers_count?: number | null;
  serial_number_pattern?: string | null;
  box_serial_number_delimiter?: string | null;
  [key: string]: unknown;
}

interface InventoryType {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface AttributeDefinition {
  id?: string;
  [key: string]: unknown;
}

interface Merchant {
  id: string;
  default_return_inventory_type_id?: string | null;
  [key: string]: unknown;
}

// 後端 search 結果中的 planned item 形狀（merchant.return_order.items 那一層）
interface SearchResultItemPlan {
  return_order_item_id?: string | null;
  fulfillment_item_id?: string | null;
  product?: Product | null;
  inventory_type?: InventoryType | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  quantity?: number;
  [key: string]: unknown;
}

// /return_receipts/search 結果（從 ReturnOrderVerification/Index.tsx 沿用的最小欄位）
interface SearchResult {
  match_count?: number;
  match_type?: string;
  return_order?: { id: string; name?: string; status?: string; [key: string]: unknown } | null;
  existing_return_order?: { id: string; [key: string]: unknown } | null;
  fulfillment?: { id: string; name?: string; number?: string; [key: string]: unknown } | null;
  merchant: Merchant;
  items?: SearchResultItemPlan[];
  active_pending_receipts?: unknown[];
  [key: string]: unknown;
}

// 接續驗收時，server 回傳的 pending receipt 內項目
interface ResumedReceiptItem {
  id?: string;
  return_order_item_id?: string | null;
  product?: Product | null;
  product_id?: string;
  inventory_type_id?: string | null;
  inventory_type?: InventoryType | null;
  batch?: string | null;
  expiration_date?: string | null;
  quantity?: number;
  received_quantity?: number;
  unplanned?: boolean;
  [key: string]: unknown;
}

interface ResumedReceipt {
  id: string;
  items?: ResumedReceiptItem[];
  [key: string]: unknown;
}

// 驗收頁的品項列（混了 planned 與 unplanned）
interface VerificationItem {
  id: string;
  product?: Product | null;
  product_id?: string;
  return_order_item_id?: string | null;
  fulfillment_item_id?: string | null;
  inventory_type_id?: string | null;
  inventory_type?: InventoryType | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  quantity?: number;
  received_quantity?: number;
  serial_numbers?: string[];
  mismatch?: boolean;
  unplanned?: boolean;
  [key: string]: unknown;
}

// verifyItem 第二個參數（VerificationModal 或 scanProduct 自動 +1 傳入的驗收資料）
interface VerifiedData {
  received_quantity: number;
  inventory_type_id?: string | null;
  inventory_type?: InventoryType | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  serial_numbers?: string[];
  product_id?: string;
  quantity?: number;
  [key: string]: unknown;
}

interface SearchResponse {
  match_count: number;
  results: SearchResult[];
  match_type: string;
}

interface ReturnReceiptAcceptanceContextValue {
  loading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  searchResult: SearchResult | null;
  items: VerificationItem[];
  scannedItem: VerificationItem | null;
  setScannedItem: React.Dispatch<React.SetStateAction<VerificationItem | null>>;
  inventoryTypes: InventoryType[];
  attributeDefinitions: AttributeDefinition[];
  merchantId: string | null;
  defaultReturnInventoryTypeId: string | null;
  anomalyMode: boolean;
  setAnomalyMode: React.Dispatch<React.SetStateAction<boolean>>;
  search: (code: string) => Promise<SearchResponse | null>;
  loadResult: (result: SearchResult) => void;
  loadResultWithReceipt: (result: SearchResult, receipt: ResumedReceipt) => void;
  scanProduct: (code: string) => Promise<void>;
  verifyItem: (itemId: string, verifiedData: VerifiedData) => void;
  reset: () => void;
}

const ReturnReceiptAcceptanceContext = createContext<ReturnReceiptAcceptanceContextValue | null>(null);

// Fast-path +1 gate：當商品要求批號/效期/製造日/序號，但 row 上沒對應資料時回 true，
// 由呼叫端改為開 modal 讓 operator 補填。serial-required 永遠 true（每 unit 需新序號）。
const needsManualInput = (row: VerificationItem): boolean => {
  const p = row.product;
  if (!p) return false;
  if ((p.required_serial_numbers_count ?? 0) > 0) return true;
  if (p.batch_management_required && !row.batch) return true;
  if ((p.validity_days || 0) > 0 && !row.expiration_date) return true;
  if (p.manufacturing_date_required && !row.manufacturing_date) return true;
  return false;
};

// 掃碼以 exact 比對：sku、主條碼 barcode、或任一別名條碼 secondary_barcodes。
// 後端也認得別名條碼，本地清單比對若只看主條碼，掃別名會被誤判成 unplanned。
// 掃碼自動 +1（scanProduct）與影片字幕（handleSubmitBarcode）共用此 helper。
export const productMatchesCode = (product: Product | null | undefined, code: string): boolean => {
  if (!product) return false;
  if (product.sku === code || product.barcode === code) return true;
  return (product.secondary_barcodes || []).includes(code);
};

interface ReturnReceiptAcceptanceProviderProps {
  children: ReactNode;
}

export const ReturnReceiptAcceptanceProvider: React.FC<ReturnReceiptAcceptanceProviderProps> = ({ children }) => {
  const { t } = useTranslation();

  // 核心狀態
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [items, setItems] = useState<VerificationItem[]>([]);
  const [scannedItem, setScannedItem] = useState<VerificationItem | null>(null);
  const [inventoryTypes, setInventoryTypes] = useState<InventoryType[]>([]);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [defaultReturnInventoryTypeId, setDefaultReturnInventoryTypeId] = useState<string | null>(null);
  const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]); // 貨主自訂屬性定義
  const [anomalyMode, setAnomalyMode] = useState(false); // 回報異常模式（one-shot）

  // 把 BE 錯誤碼翻譯成可顯示訊息；i18n key 缺失時 fallback 走 operation_failed，
  // 不把 raw machine code 露給操作員。
  const formatLoadError = useCallback(
    (errCode: string | undefined, interpolations?: Record<string, unknown>): string =>
      errCode
        ? (t(`common.error.${errCode}`, {
            ...(interpolations || {}),
            defaultValue: t('common.error.operation_failed'),
          }) as string)
        : (t('common.error.operation_failed') as string),
    [t],
  );

  // 載入貨主自訂屬性定義（同 merchant 不重複查詢）
  //
  // 錯誤路徑：BE 預期業務錯誤（success=false）與未預期 exception 都必須 surface 為
  // error state — 若靜默吞掉，attributeDefinitions 會以空陣列假裝載入成功，導致
  // 自訂屬性欄位整段不顯示，operator 不知道有東西要填，BE 收到沒帶 custom_attributes
  // 的 payload。
  const loadAttributeDefinitions = useCallback(async (mId: string) => {
    if (!mId || mId === merchantId) return;
    try {
      const response = await operatorApiFetch(`inventory_attribute_definitions`, {
        params: { merchant_id: mId }
      });
      const data = await response.json();
      if (data.success && data.data) {
        setAttributeDefinitions(data.data);
        return;
      }
      setError(formatLoadError(data.message, data.interpolations));
      soundEffects.playBuzzerError();
    } catch (err) {
      console.error('[ReturnReceiptAcceptance] Failed to load attribute definitions:', err);
      const errCode = (err as { errorCode?: string }).errorCode;
      setError(formatLoadError(errCode));
      soundEffects.playBuzzerError();
    }
  }, [merchantId, formatLoadError]);

  // 載入庫存類型（同 merchant 不重複查詢）
  //
  // 錯誤路徑：同 loadAttributeDefinitions — 若靜默吞掉，inventoryTypes 為空，operator
  // 無法選擇正確的庫存類型，會被迫送 default fallback（context line 480/510），
  // 庫存被丟到錯位置。
  const loadInventoryTypes = useCallback(async (mId: string) => {
    if (!mId || mId === merchantId) return;
    try {
      const response = await operatorApiFetch(`merchants/${mId}/merchant_inventory_types`);
      const data = await response.json();
      if (data.success && data.data) {
        const rows = (data.data as Array<{ inventory_type?: InventoryType | null }>)
          .map((mit) => mit.inventory_type)
          .filter(Boolean) as InventoryType[];
        setInventoryTypes(rows);
        return;
      }
      setError(formatLoadError(data.message, data.interpolations));
      soundEffects.playBuzzerError();
    } catch (err) {
      console.error('[ReturnReceiptAcceptance] Failed to load inventory types:', err);
      const errCode = (err as { errorCode?: string }).errorCode;
      setError(formatLoadError(errCode));
      soundEffects.playBuzzerError();
    }
  }, [merchantId, formatLoadError]);

  // 搜尋（統一入口）
  //
  // 錯誤路徑：
  //   - BE 預期業務錯誤（response.success=false 帶 error code）→ inline handle，
  //     用 `common.error.${code}` 翻譯，i18n key 缺失時 fallback 走 operation_failed
  //     而非原 raw code（避免把機器碼例如 `INVALID_PARAMETER` 直接顯示給操作員）
  //   - 查無結果 → 顯示 not_found 訊息
  //   - 未預期 exception（network drop / zod parse / JSON parse）→ catch 撈到，
  //     console.error 全 err 物件供 Sentry / dev console 追，UI 一律 operation_failed
  //
  // **不要**把 BE classifier 邏輯放在 catch（會把 FE bug 誤分類成 BE 錯誤）。
  const search = useCallback(async (code: string): Promise<SearchResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await operatorApiFetch('return_receipts/search', { params: { code } });
      const data = await response.json();

      if (!data.success) {
        const errCode = data.message;
        setError(
          errCode
            ? (t(`common.error.${errCode}`, {
                ...(data.interpolations || {}),
                defaultValue: t('common.error.operation_failed'),
              }) as string)
            : (t('common.error.operation_failed') as string)
        );
        soundEffects.playBuzzerError();
        return null;
      }

      const { match_count, results, match_type } = data.data;

      if (match_count === 0) {
        setError(t('operator.return_verification.not_found', {
          defaultValue: '找不到相關的退貨單、出貨單或物流單號'
        }) as string);
        soundEffects.playBuzzerError();
        return null;
      }

      return { match_count, results, match_type };
    } catch (err) {
      console.error('[ReturnReceiptAcceptance] search failed:', err);
      setError(t('common.error.operation_failed') as string);
      soundEffects.playBuzzerError();
      return null;
    } finally {
      setLoading(false);
    }
  }, [t]);

  // 載入搜尋結果 → 初始化品項清單
  const loadResult = useCallback((result: SearchResult) => {
    setSearchResult(result);
    setMerchantId(result.merchant.id);
    setDefaultReturnInventoryTypeId(result.merchant.default_return_inventory_type_id || null);

    const defaultInvTypeId = result.merchant.default_return_inventory_type_id;
    // 後端 `quantity` 已是「扣掉前次驗收後的剩餘應收」(commit 17f32c44f)。
    // 剩餘為 0 代表此列已被完整驗收，無事可做 → 不列出來避免雜訊。
    const initialItems: VerificationItem[] = (result.items || [])
      .filter((item) => (item.quantity || 0) > 0)
      .map((item, index) => ({
      id: `item-${index}`,
      return_order_item_id: item.return_order_item_id || null,
      fulfillment_item_id: item.fulfillment_item_id || null,
      product: item.product,
      product_id: item.product?.id,
      inventory_type_id: defaultInvTypeId || item.inventory_type?.id || null,
      inventory_type: defaultInvTypeId ? null : item.inventory_type,
      batch: item.batch,
      expiration_date: item.expiration_date,
      manufacturing_date: item.manufacturing_date ?? null,
      custom_attributes: item.custom_attributes ?? null,
      quantity: item.quantity || 0,
      received_quantity: 0,
      serial_numbers: [],
      mismatch: false,
    }));

    setItems(initialItems);
    loadInventoryTypes(result.merchant.id);
    loadAttributeDefinitions(result.merchant.id);
    soundEffects.playScan();
  }, [loadInventoryTypes, loadAttributeDefinitions]);

  // Resume variant — overlays a server-side pending receipt's items onto the
  // initial item set so the operator sees prior progress when接續 a draft.
  const loadResultWithReceipt = useCallback((result: SearchResult, receipt: ResumedReceipt) => {
    setSearchResult(result);
    setMerchantId(result.merchant.id);
    setDefaultReturnInventoryTypeId(result.merchant.default_return_inventory_type_id || null);

    const defaultInvTypeId = result.merchant.default_return_inventory_type_id;
    // 同 loadResult：剩餘應收為 0 的列省略，receipt overlay 仍會處理 draft 進行中的 row。
    const initialItems: VerificationItem[] = (result.items || [])
      .filter((item) => (item.quantity || 0) > 0)
      .map((item, index) => ({
        id: `item-${index}`,
        return_order_item_id: item.return_order_item_id || null,
        fulfillment_item_id: item.fulfillment_item_id || null,
        product: item.product,
        product_id: item.product?.id,
        inventory_type_id: defaultInvTypeId || item.inventory_type?.id || null,
        inventory_type: defaultInvTypeId ? null : item.inventory_type,
        batch: item.batch,
        expiration_date: item.expiration_date,
        manufacturing_date: item.manufacturing_date ?? null,
        custom_attributes: item.custom_attributes ?? null,
        quantity: item.quantity || 0,
        received_quantity: 0,
        serial_numbers: [],
        mismatch: false,
      }));

    const receiptItems = receipt?.items || [];
    // Cap matching to the *original* initialItems length. Without this, a row
    // we push below (because phase 1+2 missed) becomes visible to the next
    // rri's findIndex — same product/batch/exp collides and the second rri
    // overwrites the freshly-pushed row instead of becoming its own row.
    const initialLen = initialItems.length;
    const consumed = new Set<number>();
    receiptItems.forEach((rri) => {
      // Phase 1: exact ROI id match — needed when result.items has already
      // filtered the ROI out (remaining=0) but receipt still holds prior
      // progress on it. Backend exposes `return_order_item_id` on rri so FE
      // can do this without re-matching by tuple.
      let targetIdx = -1;
      if (rri.return_order_item_id) {
        targetIdx = initialItems.findIndex((it, idx) =>
          idx < initialLen && !consumed.has(idx) &&
          it.return_order_item_id === rri.return_order_item_id
        );
      }
      // Phase 2: tuple match — inventory_type_id is part of the key so a
      // planned 良品 row isn't overwritten by an anomaly 瑕疵 row sharing the
      // same product/batch/exp.
      if (targetIdx < 0) {
        targetIdx = initialItems.findIndex((it, idx) =>
          idx < initialLen && !consumed.has(idx) &&
          it.product_id === rri.product_id &&
          (it.inventory_type_id || null) === (rri.inventory_type_id || null) &&
          (it.batch || null) === (rri.batch || null) &&
          (it.expiration_date || null) === (rri.expiration_date || null)
        );
      }
      if (targetIdx >= 0) {
        consumed.add(targetIdx);
        const target = initialItems[targetIdx];
        Object.assign(target, {
          received_quantity: rri.received_quantity || 0,
          inventory_type_id: rri.inventory_type_id || target.inventory_type_id,
          inventory_type: rri.inventory_type || target.inventory_type,
          batch: rri.batch ?? target.batch,
          expiration_date: rri.expiration_date ?? target.expiration_date,
          unplanned: false,
        });
      } else {
        initialItems.push({
          id: `resume-${rri.id}`,
          return_order_item_id: rri.return_order_item_id || null,
          product: rri.product,
          product_id: rri.product_id,
          inventory_type_id: rri.inventory_type_id || null,
          inventory_type: rri.inventory_type || null,
          batch: rri.batch || null,
          expiration_date: rri.expiration_date || null,
          quantity: rri.quantity || 0,
          received_quantity: rri.received_quantity || 0,
          serial_numbers: [],
          mismatch: false,
          unplanned: rri.unplanned ?? rri.return_order_item_id == null,
        });
      }
    });

    setItems(initialItems);
    loadInventoryTypes(result.merchant.id);
    loadAttributeDefinitions(result.merchant.id);
    soundEffects.playScan();
  }, [loadInventoryTypes, loadAttributeDefinitions]);

  // 驗收確認（從 VerificationModal 或自動 +1 呼叫）
  const verifyItem = useCallback((itemId: string, verifiedData: VerifiedData) => {
    // 從快取解析 inventory_type 物件（用於表格顯示名稱）
    const resolvedInvType = inventoryTypes.find(it => it.id === verifiedData.inventory_type_id) || null;

    setItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === itemId);

      if (existingIndex >= 0) {
        return prev.map(i => {
          if (i.id !== itemId) return i;
          const originalInvTypeId = i.inventory_type?.id || i.inventory_type_id;
          const mismatch = originalInvTypeId !== verifiedData.inventory_type_id ||
                           i.batch !== verifiedData.batch ||
                           i.expiration_date !== verifiedData.expiration_date;
          return {
            ...i,
            received_quantity: verifiedData.received_quantity,
            inventory_type_id: verifiedData.inventory_type_id,
            inventory_type: resolvedInvType,
            batch: verifiedData.batch,
            expiration_date: verifiedData.expiration_date,
            manufacturing_date: verifiedData.manufacturing_date,
            custom_attributes: verifiedData.custom_attributes,
            serial_numbers: verifiedData.serial_numbers || [],
            mismatch,
          };
        });
      } else {
        return [...prev, {
          ...verifiedData,
          id: itemId,
          inventory_type: resolvedInvType,
          mismatch: false,
          unplanned: true,
        }];
      }
    });

    setScannedItem(null);
    soundEffects.playScan();
  }, [inventoryTypes]);

  // 掃描商品條碼
  const scanProduct = useCallback(async (code: string) => {
    // 每次掃描開頭清掉上一次的錯誤訊息：成功 → 不會再被設回去、橫幅消失；
    // 失敗 → 在後續分支被換成這次的新訊息（不會與舊訊息混淆 / sticky）。
    setError(null);

    // 1. 在品項清單中尋找
    const allMatched = items.filter(item => productMatchesCode(item.product, code));

    if (allMatched.length > 0) {
      // 異常模式 → 開 Modal（one-shot，用完自動關）
      if (anomalyMode) {
        const targetItem = allMatched.find(i => i.received_quantity === 0) || allMatched[0];
        // 建立拆分行讓操作員在 Modal 中選擇 inventory_type
        const splitItem: VerificationItem = {
          id: `anomaly-${Date.now()}`,
          product: targetItem.product,
          product_id: targetItem.product_id,
          inventory_type_id: null, // 異常品不預填 inventory_type，強制操作員選擇
          inventory_type: null,
          batch: targetItem.batch,
          expiration_date: targetItem.expiration_date,
          quantity: 0,
          received_quantity: 1,
          serial_numbers: [],
          mismatch: false,
          unplanned: true,
        };
        setScannedItem(splitItem);
        setAnomalyMode(false); // one-shot
        soundEffects.playScan();
        return;
      }

      // 正常模式 → 自動 +1
      // 找第一個尚未填滿的 planned row（received < expected）。
      // 不能用 `rq === 0`：當同一品項有兩列 planned（例：A 預期 2 + A 預期 3），
      // 第二次掃時第一列 rq=1 但還沒滿，若直接看 rq===0 會錯誤地跳到第二列。
      const targetRow = allMatched.find(
        i => !i.unplanned && (i.received_quantity || 0) < (i.quantity || 0)
      );
      if (targetRow) {
        // 在 fast-path +1 之前先 gate：若商品要求批號/效期/製造日/序號，但 row 上沒對應資料，
        // 就改開 modal 讓 operator 補填。RO 只攜帶 batch / expiration_date / inventory_type，
        // 其他欄位永遠是空的；serial-required 商品因每 unit 需新序號，永遠走 modal。
        if (needsManualInput(targetRow)) {
          setScannedItem({
            ...targetRow,
            received_quantity: (targetRow.received_quantity || 0) + 1,
            inventory_type_id: targetRow.inventory_type_id || defaultReturnInventoryTypeId || null,
          });
          soundEffects.playScan();
          return;
        }
        const isFirstScanOnRow = (targetRow.received_quantity || 0) === 0;
        verifyItem(targetRow.id, {
          ...targetRow,
          received_quantity: (targetRow.received_quantity || 0) + 1,
          // 只在「這一列第一次被掃」時把 inventory_type_id 套用 default；
          // 之後續掃保留使用者已選的 inv_type，避免 modal 改過又被覆寫。
          ...(isFirstScanOnRow
            ? { inventory_type_id: defaultReturnInventoryTypeId || targetRow.inventory_type_id }
            : {}),
        });
        soundEffects.playScan();
        return;
      }

      // 已有驗收記錄 → 找到主要行，+1
      const primaryItem = allMatched.find(i => !i.unplanned) || allMatched[0];
      const totalExpected = allMatched.filter(i => !i.unplanned).reduce((sum, i) => sum + (i.quantity || 0), 0);
      const totalReceived = allMatched.reduce((sum, i) => sum + (i.received_quantity || 0), 0);

      if (totalExpected > 0 && totalReceived >= totalExpected) {
        // 已全數驗收 → 開 Modal（可能需要拆分 inventory_type）
        const splitItem: VerificationItem = {
          id: `split-${Date.now()}`,
          product: primaryItem.product,
          product_id: primaryItem.product_id,
          inventory_type_id: defaultReturnInventoryTypeId || primaryItem.inventory_type_id,
          inventory_type: null,
          batch: primaryItem.batch,
          expiration_date: primaryItem.expiration_date,
          quantity: 0,
          received_quantity: 1,
          serial_numbers: [],
          mismatch: false,
          unplanned: true,
        };
        setScannedItem(splitItem);
        soundEffects.playScan();
        return;
      }

      // 尚未全數驗收 → +1 到主要行（但若需手動輸入 → 改開 drawer，與 L459 同步）
      // 觸發情境：matched rows 全為 unplanned（totalExpected = 0），上方 splitItem
      // 分支因 `totalExpected > 0` 不成立而被略過。沒這層 gate 的話，
      // serial-required 商品的第 N 次掃描會靜默 +1，無法捕捉新序號。
      if (needsManualInput(primaryItem)) {
        setScannedItem({
          ...primaryItem,
          received_quantity: (primaryItem.received_quantity || 0) + 1,
          inventory_type_id: primaryItem.inventory_type_id || defaultReturnInventoryTypeId || null,
        });
        soundEffects.playScan();
        return;
      }
      verifyItem(primaryItem.id, {
        ...primaryItem,
        received_quantity: (primaryItem.received_quantity || 0) + 1,
      });
      soundEffects.playScan();
      return;
    }

    // 2. 不在清單中 → 用「精確匹配 sku / barcode（含 product_barcodes）」查商品。
    // 不能用 `search=`：那是 sku+name 的 substring 模糊比對，例如掃 "123"
    // 會配出任何 sku 含 "123" 的商品（甚至到 limit=1 取到不相關項目）。
    // controller 的 `sku_or_barcode=` 走 EQ + ProductBarcode EXISTS。
    try {
      setLoading(true);
      const response = await operatorApiFetch('products', {
        params: { sku_or_barcode: code, limit: 1, merchant_id: merchantId }
      });
      const data = await response.json();

      if (data.success && data.data && data.data.length > 0) {
        const product = data.data[0];
        const newItem: VerificationItem = {
          id: `unplanned-${Date.now()}`,
          product: {
            id: product.id, sku: product.sku, name: product.name,
            barcode: product.barcode,
            validity_days: product.validity_days,
            batch_management_required: product.batch_management_required,
            manufacturing_date_required: product.manufacturing_date_required,
            required_serial_numbers_count: product.required_serial_numbers_count,
            serial_number_pattern: product.serial_number_pattern,
            box_serial_number_delimiter: product.box_serial_number_delimiter,
          },
          product_id: product.id,
          inventory_type_id: defaultReturnInventoryTypeId || null,
          inventory_type: null,
          batch: null,
          expiration_date: null,
          quantity: 0,
          received_quantity: 0,
          serial_numbers: [],
          mismatch: false,
          unplanned: true,
        };

        setScannedItem(newItem);
        soundEffects.playScan();
        message.info(t('operator.return_verification.unplanned_item', {
          defaultValue: '此商品不在預期清單中'
        }) as string);
      } else {
        setError(t('operator.return_verification.product_not_found', {
          defaultValue: '找不到商品（條碼或品號：{{code}}）',
          code,
        }) as string);
        soundEffects.playBuzzerError();
      }
    } catch (err) {
      console.error('[ReturnReceiptAcceptance] scanProduct failed:', err);
      const errCode = (err as { errorCode?: string }).errorCode;
      setError(
        errCode
          ? (t(`common.error.${errCode}`, { defaultValue: t('common.error.operation_failed') as string }) as string)
          : (t('common.error.operation_failed') as string)
      );
      soundEffects.playBuzzerError();
    } finally {
      setLoading(false);
    }
  }, [items, defaultReturnInventoryTypeId, merchantId, anomalyMode, verifyItem, t]);

  // 重置（保留 inventoryTypes 和 merchantId — 退貨常連續處理同一貨主）
  const reset = useCallback(() => {
    setSearchResult(null);
    setItems([]);
    setScannedItem(null);
    setError(null);
    setAnomalyMode(false);
    // 不清除 merchantId, defaultReturnInventoryTypeId, inventoryTypes
    // 下一張單據載入時，loadResult 會更新；loadInventoryTypes 會判斷是否需要重新查詢
  }, []);

  const value: ReturnReceiptAcceptanceContextValue = {
    loading, error, setError,
    searchResult, items, scannedItem, setScannedItem,
    inventoryTypes, attributeDefinitions, merchantId, defaultReturnInventoryTypeId,
    anomalyMode, setAnomalyMode,
    search, loadResult, loadResultWithReceipt, scanProduct, verifyItem, reset,
  };

  return (
    <ReturnReceiptAcceptanceContext.Provider value={value}>
      {children}
    </ReturnReceiptAcceptanceContext.Provider>
  );
};

export function useReturnReceiptAcceptance(): ReturnReceiptAcceptanceContextValue {
  const context = useContext(ReturnReceiptAcceptanceContext);
  if (!context) throw new Error('useReturnReceiptAcceptance must be used within ReturnReceiptAcceptanceProvider');
  return context;
}

export default ReturnReceiptAcceptanceContext;
